import { BOARD_PAGE_SIZE, clampBoardPage, type LeaderboardRow, type ShooterGender } from './shooting';

/** Screens at least this wide (TVs, laptops) show the page an admin picked; narrower ones show everyone. */
export const BIG_SCREEN_QUERY = '(min-width: 1024px)';

/** All / Men / Women filter on the board and the admin list. */
export type GenderFilter = 'all' | ShooterGender;

export function parseGenderFilter(value: string | null | undefined): GenderFilter {
  return value === 'male' || value === 'female' ? value : 'all';
}

/**
 * Only the shooters of one group, re-ranked within it. Rows arrive in overall order (same tie-break), so a
 * shooter's group rank is 1 + the group shooters strictly ahead of them; ties keep sharing a rank.
 * Unscored shooters (rank 0) stay unranked. 'all' returns the rows untouched.
 */
export function filterByGender<T extends { rank: number; gender?: ShooterGender | null }>(rows: T[], filter: GenderFilter): T[] {
  if (filter === 'all') return rows;
  const group = rows.filter((row) => row.gender === filter);
  return group.map((row) =>
    row.rank > 0 ? { ...row, rank: 1 + group.filter((other) => other.rank > 0 && other.rank < row.rank).length } : row,
  );
}

export interface BoardView {
  rows: LeaderboardRow[];
  /** True when only the admin-picked page is shown. */
  paged: boolean;
  page: number;
}

/**
 * Which rows the public board shows. Phones (and anyone searching or tapping "Show all") get the full
 * list; big screens get the admin-picked page of BOARD_PAGE_SIZE ranks. Nothing pages by itself.
 */
export function boardView(
  rows: LeaderboardRow[],
  options: { bigScreen: boolean; query: string; showAll: boolean; displayPage: number; gender?: GenderFilter },
): BoardView {
  const gender = options.gender ?? 'all';
  rows = filterByGender(rows, gender);
  const needle = options.query.trim().toLowerCase();
  if (needle) {
    return {
      rows: rows.filter(
        (row) => row.name.toLowerCase().includes(needle) || (row.bib ?? '').toLowerCase().includes(needle),
      ),
      paged: false,
      page: 0,
    };
  }
  // A Men / Women view shows the whole group; the admin's page applies to the overall board only.
  if (!options.bigScreen || options.showAll || gender !== 'all' || rows.length <= BOARD_PAGE_SIZE) {
    return { rows, paged: false, page: 0 };
  }
  const page = clampBoardPage(options.displayPage, rows.length);
  return { rows: rows.slice(page * BOARD_PAGE_SIZE, (page + 1) * BOARD_PAGE_SIZE), paged: true, page };
}
